import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Modal, RadioGroup, FormControlLabel, Radio, FormControl, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { API, graphqlOperation, Storage } from 'aws-amplify';
import { createQuestionGroup, updateQuestionGroup, deleteQuestionGroup } from '../graphql/mutations';
import { listQuestionGroups, listQuestionGroupTypes, listRoadmapGroups } from '../graphql/queries';
import { v4 as uuidv4 } from 'uuid';
import { makeStyles } from '@material-ui/core/styles';
import S3Image from './S3Image';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(6),
    },
  },
  textField: {
    margin: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '50%',
    },
  },
  uploadButton: {
    margin: theme.spacing(1),
  },
  submitButton: {
    margin: theme.spacing(3, 0),
  },
  tableContainer: {
    boxShadow: '0px 8px 16px 0px rgba(0,0,0,0.1)',
    borderRadius: '12px',
    overflow: 'hidden',
    marginTop: '32px',
    backgroundColor: '#f9f9f9',
  },
  tableHeader: {
    backgroundColor: '#01b5e2',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '1rem',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#e5f7fb',
    },
  },
  tableCell: {
    padding: theme.spacing(1.5, 2),
    fontSize: '0.9rem',
  },
  actionButton: {
    color: '#01b5e2',
    borderColor: '#01b5e2',
    '&:hover': {
      backgroundColor: '#01b5e2',
      color: 'white',
    }
  },
  modal: {
    position: 'absolute',
    width: '40%',
    minHeight: '40%',
    backgroundColor: theme.palette.background.paper,
    border: `4px solid #00b5e2`,
    borderRadius: '15px',
    padding: theme.spacing(4),
    top: `50%`,
    left: `50%`,
    transform: `translate(-50%, -50%)`,
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalTitle: {
    color: '#00b5e2',
    fontWeight: 'bold',
    fontSize: '1.5rem',
  },
  modalSubtitle: {
    color: 'grey',
    fontSize: '1rem',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  closeButton: {
    color: '#00b5e2',
  },
  changeStatusButton: {
    color: '#fff',
    backgroundColor: '#00b5e2',
    '&:hover': {
      backgroundColor: '#0094c6',
    },
    borderColor: '#00b5e2',
  },
}));

const AddQuestionGroupForm = () => {
  const classes = useStyles();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('');
  const [thumbnail, setThumbnail] = useState(null);
  const [thumbnailKey, setThumbnailKey] = useState('');
  const [groupLevel, setGroupLevel] = useState('');
  const [questionGroups, setQuestionGroups] = useState([]);
  const [editingQuestionGroupId, setEditingQuestionGroupId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [roadmapGroups, setRoadmapGroups] = useState([]);
  const [selectedRoadmapGroupId, setSelectedRoadmapGroupId] = useState('');
  const [questionGroupTypes, setQuestionGroupTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('');
  const [currentQuestionGroupId, setCurrentQuestionGroupId] = useState('');

  useEffect(() => {
    fetchQuestionGroups();
    fetchQuestionGroupTypes();
    fetchRoadmapGroups();
  }, []);

  const fetchQuestionGroups = async () => {
    const response = await API.graphql(graphqlOperation(listQuestionGroups));
    setQuestionGroups(response.data.listQuestionGroups.items);
  };

  const fetchQuestionGroupTypes = async () => {
    const response = await API.graphql(graphqlOperation(listQuestionGroupTypes));
    setQuestionGroupTypes(response.data.listQuestionGroupTypes.items);
  };

  const fetchRoadmapGroups = async () => {
    const response = await API.graphql(graphqlOperation(listRoadmapGroups));
    setRoadmapGroups(response.data.listRoadmapGroups.items);
  };

  const getTypeNameById = (id) => {
    const type = questionGroupTypes.find((type) => type.id === id);
    return type ? type.name : 'Unknown';
  };

  const handleTypeChange = (event) => {
    const selectedType = questionGroupTypes.find((type) => type.name === event.target.value);
    if (selectedType) {
      setType(selectedType.name);
      setSelectedTypeId(selectedType.id);
    }
  };

  const handleGroupLevelChange = (event) => {
    setGroupLevel(event.target.value);
  };

  const handleRoadmapGroupChange = (event) => {
    setSelectedRoadmapGroupId(event.target.value);
  };

  const handleDelete = async () => {
    await API.graphql(graphqlOperation(deleteQuestionGroup, { input: { id: deleteTargetId } }));
    setDeleteDialogOpen(false);
    setDeleteTargetId(null);
    fetchQuestionGroups();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    let newThumbnailKey = '';

    if (thumbnail) {
      const ext = thumbnail.name.split('.').pop();
      newThumbnailKey = `thumbnails/${uuidv4()}.${ext}`;
      await Storage.put(newThumbnailKey, thumbnail);
      console.log("key", newThumbnailKey);
      setThumbnailKey(newThumbnailKey);
    }

    if (editingQuestionGroupId) {
      await API.graphql(
        graphqlOperation(updateQuestionGroup, {
          input: {
            id: editingQuestionGroupId,
            name: name,
            description: description,
            type: 'ROADMAP',
            imageS3ObjectKey: newThumbnailKey,
            deleted: false,
            questionGroupQuestionGroupTypesId: selectedTypeId,
            groupLevel: parseInt(groupLevel),
            questionGroupRoadmapGroupId: selectedRoadmapGroupId || null,
          },
        })
      );
    } else {
      await API.graphql(
        graphqlOperation(createQuestionGroup, {
          input: {
            name,
            description,
            type: 'ROADMAP',
            imageS3ObjectKey: newThumbnailKey || null,
            deleted: false,
            status: 'DRAFT',
            questionGroupQuestionGroupTypesId: selectedTypeId,
            groupLevel: parseInt(groupLevel),
            questionGroupRoadmapGroupId: selectedRoadmapGroupId || null,
          },
        })
      );
    }

    setName('');
    setDescription('');
    setType('');
    setThumbnail(null);
    setGroupLevel('');
    setEditingQuestionGroupId(null);
    setThumbnailKey(null);
    setSelectedRoadmapGroupId('');
    fetchQuestionGroups();
  };

  const handleEdit = (questionGroupId) => {
    const questionGroup = questionGroups.find((group) => group.id === questionGroupId);
    if (questionGroup) {
      setName(questionGroup.name);
      setDescription(questionGroup.description);
      setThumbnailKey(questionGroup.imageS3ObjectKey);
      setGroupLevel(questionGroup.groupLevel);

      const typeObj = questionGroupTypes.find((type) => type.id === questionGroup.questionGroupQuestionGroupTypesId);
      if (typeObj) {
        setType(typeObj.name);
        setSelectedTypeId(typeObj.id);
      } else {
        setType('');
        setSelectedTypeId('');
      }

      setSelectedRoadmapGroupId(questionGroup.questionGroupRoadmapGroupId || '');
      setEditingQuestionGroupId(questionGroupId);
    }
  };

  const handleCancelEdit = () => {
    setName('');
    setDescription('');
    setType('');
    setThumbnail(null);
    setGroupLevel('');
    setEditingQuestionGroupId(null);
    setThumbnailKey(null);
    setSelectedRoadmapGroupId('');
  };

  const handleThumbnailChange = (event) => {
    setThumbnail(event.target.files[0]);
  };

  const handleChangeStatus = (questionGroupId, status) => {
    setCurrentQuestionGroupId(questionGroupId);
    setCurrentStatus(status || 'DRAFT');
    setOpenModal(true);
  };

  const handleStatusChange = async (event) => {
    try {
      await API.graphql(
        graphqlOperation(updateQuestionGroup, {
          input: { id: currentQuestionGroupId, status: event.target.value },
        })
      );
      setOpenModal(false);
      fetchQuestionGroups();
    } catch (error) {
      console.error('Error updating status', error);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <div>
      <Box className={classes.root} component="form" onSubmit={handleSubmit}>
        <Typography variant="h4" component="h1" gutterBottom>
          Add Question Group
        </Typography>
        <TextField
          className={classes.textField}
          required
          label="Group Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <TextField
          className={classes.textField}
          required
          label="Description"
          multiline
          minRows={4}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
        <TextField
          className={classes.textField}
          select
          required
          label="Type"
          value={type}
          onChange={handleTypeChange}
        >
          {questionGroupTypes.map((type) => (
            <MenuItem key={type.id} value={type.name}>
              {type.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          className={classes.textField}
          select
          required
          label="Group Level"
          value={groupLevel}
          onChange={handleGroupLevelChange}
        >
          {[1, 2, 3, 4, 5].map((level) => (
            <MenuItem key={level} value={level}>
              {`Group Level ${level}`}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          className={classes.textField}
          select
          label="Roadmap Group"
          value={selectedRoadmapGroupId}
          onChange={handleRoadmapGroupChange}
        >
          {roadmapGroups.map((roadmapGroup) => (
            <MenuItem key={roadmapGroup.id} value={roadmapGroup.id}>
              {roadmapGroup.name}
            </MenuItem>
          ))}
        </TextField>
        <Button
          className={classes.uploadButton}
          variant="contained"
          component="label"
          startIcon={<CloudUploadIcon />}
        >
          Upload Image
          <input type="file" hidden onChange={handleThumbnailChange} />
        </Button>
        {thumbnail && <Typography>{thumbnail.name}</Typography>}
        {thumbnailKey && (
          <Box mt={2}>
            <Typography variant="h6">Current Thumbnail Preview:</Typography>
            <S3Image S3Key={thumbnailKey} S3height={200} />
          </Box>
        )}
        <Button type="submit" variant="contained" color="primary">
          {editingQuestionGroupId ? 'Update' : 'Submit'}
        </Button>
        {editingQuestionGroupId && (
          <Button variant="outlined" color="secondary" onClick={handleCancelEdit} style={{ marginTop: '10px' }}>
            Cancel
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell className={classes.tableCell}>Name</TableCell>
              <TableCell className={classes.tableCell}>Description</TableCell>
              <TableCell className={classes.tableCell}>Type</TableCell>
              <TableCell className={classes.tableCell}>Group Level</TableCell>
              <TableCell className={classes.tableCell}>Roadmap Group</TableCell>
              <TableCell className={classes.tableCell}>Change Status</TableCell>
              <TableCell className={classes.tableCell}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {questionGroups.map((questionGroup) => (
              <TableRow key={questionGroup.id} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>{questionGroup.name}</TableCell>
                <TableCell className={classes.tableCell}>{questionGroup.description}</TableCell>
                <TableCell className={classes.tableCell}>{getTypeNameById(questionGroup.questionGroupQuestionGroupTypesId)}</TableCell>
                <TableCell className={classes.tableCell}>{questionGroup.groupLevel}</TableCell>
                <TableCell className={classes.tableCell}>
                  {roadmapGroups.find((rg) => rg.id === questionGroup.questionGroupRoadmapGroupId)?.name || 'None'}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Button
                    variant="contained"
                    className={classes.changeStatusButton}
                    onClick={() => handleChangeStatus(questionGroup.id, questionGroup.status)}
                  >
                    Change Status
                  </Button>
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Button variant="outlined" className={classes.actionButton} onClick={() => handleEdit(questionGroup.id)}>Edit</Button>
                  <Button variant="outlined" className={classes.actionButton} onClick={() => { setDeleteDialogOpen(true); setDeleteTargetId(questionGroup.id); }}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this question group?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="secondary">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Modal open={openModal} onClose={handleCloseModal}>
        <Box className={classes.modal}>
          <Box className={classes.modalHeader}>
            <Typography variant="h6" className={classes.modalTitle}>
              Change Status
            </Typography>
            <IconButton className={classes.closeButton} onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="subtitle1" className={classes.modalSubtitle}>
            Changing the status will have an instant impact on content visible to the users.
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup className={classes.radioGroup} value={currentStatus} onChange={handleStatusChange}>
              <FormControlLabel value="DRAFT" control={<Radio />} label="Draft" />
              <FormControlLabel value="PUBLISHED" control={<Radio />} label="Published" />
              <FormControlLabel value="TESTING" control={<Radio />} label="Testing" />
            </RadioGroup>
          </FormControl>
        </Box>
      </Modal>
    </div>
  );
};

export default AddQuestionGroupForm;
